import { useEffect, useMemo, useRef, useState } from 'react'
import arrowRight from '../../../../assets/icons/arrowRight.svg'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../redux'
import ivyLeagueSlice from '../../../../redux/slice/ivyLeague.slice'
import { config } from '../../../../config'
import type { TransportMode } from '../../../../types/ivyLeague'

const inferDaysPerWeekFromProgramName = (programName: string): 3 | 4 | 5 | null => {
  const n = (programName ?? '').toLowerCase()
  if (n.includes('mwf')) return 3
  if (n.includes('3 day')) return 3
  if (n.includes('4 day')) return 4
  if (n.includes('5 day')) return 5
  if (n.includes('full day')) return 5
  return null
}

const formatMoney = (amount: number): string => {
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const parseGradeNumber = (grade: string): number | null => {
  const m = String(grade ?? '').match(/\d+/)
  if (!m) return null
  const n = parseInt(m[0], 10)
  return Number.isFinite(n) ? n : null
}

const isHybridEligibleGrade = (grade: string): boolean => {
  const n = parseGradeNumber(grade)
  return n != null && n >= 7 && n <= 10
}

const isToddlerGrade = (grade: string): boolean => {
  return (grade ?? '').toLowerCase().includes('2 years old')
}

const HYBRID_TOTAL_WEEKS_MAX = 8
const HYBRID_OFF_CAMPUS_MIN_WEEKS = 2
const HYBRID_ON_CAMPUS_MIN_WEEKS = 1
const HYBRID_TOTAL_WEEKS_MIN = 3

const HYBRID_ON_CAMPUS_WEEK_OPTIONS = [1, 2, 3, 4, 5, 6]
const HYBRID_OFF_CAMPUS_WEEK_OPTIONS = [2, 3, 4, 5, 6, 7]

export const Step2 = () => {
  const dispatch: AppDispatch = useDispatch()
  const { kids, adminConfiguration } = useSelector((state: RootState) => state.ivyLeague)

  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null)

  const closeDropdown = () => setOpenDropdownKey(null)
  const isDropdownOpen = (key: string) => openDropdownKey === key
  const toggleDropdown = (key: string, disabled?: boolean) => {
    if (disabled) return
    setOpenDropdownKey((prev) => (prev === key ? null : key))
  }

  const dropdownContainerRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const setDropdownContainerRef = (key: string) => (el: HTMLDivElement | null) => {
    if (el) dropdownContainerRefs.current.set(key, el)
    else dropdownContainerRefs.current.delete(key)
  }

  useEffect(() => {
    if (!openDropdownKey) return
    const el = dropdownContainerRefs.current.get(openDropdownKey)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [openDropdownKey])

  const availableGrades = useMemo(
    () => Object.keys(adminConfiguration?.programPricing || {}),
    [adminConfiguration]
  )

  // Auto-select single-option paths (e.g. Toddlers → OnCampus → Mini → 8)
  useEffect(() => {
    const pricingByGrade = adminConfiguration?.programPricing
    if (!pricingByGrade) return

    kids.forEach((kid) => {
      if (!kid.grade) return
      const availableProgramsForGrade = pricingByGrade?.[kid.grade]
      if (!availableProgramsForGrade) return

      // Auto-select Parent transport for Toddlers
      if (isToddlerGrade(kid.grade) && (kid.transportMode ?? 'Parent') !== 'Parent') {
        dispatch(ivyLeagueSlice.actions.setKidTransportMode({
          kidId: kid.id,
          transportMode: 'Parent'
        }))
      }

      kid.programs.forEach((program) => {
        const typeOptions = Object.keys(availableProgramsForGrade)
        const inferredType = program.programType || (typeOptions.length === 1 ? typeOptions[0] : '')
        if (!program.programType && inferredType) {
          dispatch(ivyLeagueSlice.actions.setProgramTypeOfaKid({
            kidId: kid.id,
            programId: program.programId,
            programType: inferredType
          }))
        }

        if (!inferredType) return
        const nameOptions = Object.keys(availableProgramsForGrade?.[inferredType] ?? {})
        const inferredName = program.programName || (nameOptions.length === 1 ? nameOptions[0] : '')
        if (!program.programName && inferredName) {
          dispatch(ivyLeagueSlice.actions.setProgramNameOfaKid({
            kidId: kid.id,
            programId: program.programId,
            programName: inferredName
          }))
        }

        if (!inferredName) return
        const weekOptions = Object.keys(availableProgramsForGrade?.[inferredType]?.[inferredName] ?? {})
          .map((w) => parseInt(w, 10))
          .filter((n) => !isNaN(n))
          .sort((a, b) => a - b)

        if ((program.weekCount ?? 0) === 0 && weekOptions.length === 1) {
          dispatch(ivyLeagueSlice.actions.setWeekCountOfaKid({
            kidId: kid.id,
            programId: program.programId,
            weekCount: weekOptions[0]
          }))
        }
      })
    })
  }, [adminConfiguration, dispatch, kids])

  // Keep add-on flags consistent with selected programs.
  useEffect(() => {
    kids.forEach((kid) => {
      const programs = kid.programs ?? []
      const hasOnCampus = programs.some((p) => p.programType === 'OnCampus')
      const hasOffCampus = programs.some((p) => p.programType === 'OffCampus')

      const onCampusDays = programs
        .filter((p) => p.programType === 'OnCampus')
        .map((p) => inferDaysPerWeekFromProgramName(p.programName))
        .find((v) => v != null) ?? null

      const eligibleForReduceFiveToFour = hasOnCampus && onCampusDays === 5

      if (!eligibleForReduceFiveToFour && (kid.usesExtendedHours ?? false)) {
        dispatch(ivyLeagueSlice.actions.setKidUsesExtendedHours({
          kidId: kid.id,
          usesExtendedHours: false
        }))
      }

      if (!hasOffCampus && (kid.finaleTrip ?? false)) {
        dispatch(ivyLeagueSlice.actions.setKidFinaleTrip({
          kidId: kid.id,
          finaleTrip: false
        }))
      }

      if (!hasOnCampus && (kid.transportMode ?? 'Parent') === 'Bus') {
        dispatch(ivyLeagueSlice.actions.setKidTransportMode({
          kidId: kid.id,
          transportMode: 'Parent'
        }))
      }

      // Hybrid safety: enforce week bounds and total-week cap.
      const gradeNum = parseGradeNumber(kid.grade)
      const is7to10 = gradeNum != null && gradeNum >= 7 && gradeNum <= 10
      if (is7to10 && hasOnCampus && hasOffCampus) {
        const onProgram = programs.find((p) => p.programType === 'OnCampus')
        const offProgram = programs.find((p) => p.programType === 'OffCampus')
        if (!onProgram || !offProgram) return

        const onWeeks = onProgram.weekCount ?? 0
        const offWeeks = offProgram.weekCount ?? 0

        if (onWeeks > 0 && (onWeeks < HYBRID_ON_CAMPUS_MIN_WEEKS || onWeeks > 6)) {
          dispatch(ivyLeagueSlice.actions.setWeekCountOfaKid({ kidId: kid.id, programId: onProgram.programId, weekCount: 0 }))
        }
        if (offWeeks > 0 && (offWeeks < HYBRID_OFF_CAMPUS_MIN_WEEKS || offWeeks > 7)) {
          dispatch(ivyLeagueSlice.actions.setWeekCountOfaKid({ kidId: kid.id, programId: offProgram.programId, weekCount: 0 }))
        }

        const total = (onWeeks > 0 ? onWeeks : 0) + (offWeeks > 0 ? offWeeks : 0)
        if (onWeeks > 0 && offWeeks > 0 && total < HYBRID_TOTAL_WEEKS_MIN) {
          dispatch(ivyLeagueSlice.actions.setWeekCountOfaKid({ kidId: kid.id, programId: offProgram.programId, weekCount: 0 }))
        }
        if (onWeeks > 0 && offWeeks > 0 && total > HYBRID_TOTAL_WEEKS_MAX) {
          // Clear the larger side to force a valid re-selection.
          const programToClear = onWeeks >= offWeeks ? onProgram : offProgram
          dispatch(ivyLeagueSlice.actions.setWeekCountOfaKid({ kidId: kid.id, programId: programToClear.programId, weekCount: 0 }))
        }
      }
    })
  }, [dispatch, kids])

  return (
    <>
      <div className="step2">
        {kids.map((kid) => {
        const availableProgramsForGrade = kid?.grade ? adminConfiguration?.programPricing?.[kid?.grade] : undefined
        const allProgramTypes = Object.keys(availableProgramsForGrade ?? {})

        const supportsHybridOption =
          isHybridEligibleGrade(kid.grade) &&
          allProgramTypes.includes('OnCampus') &&
          allProgramTypes.includes('OffCampus')

        const selectedTypes = (kid.programs ?? [])
          .map((p) => p.programType)
          .filter((t) => t && t.length > 0)
        const uniqueSelectedTypes = Array.from(new Set(selectedTypes))
        const remainingTypes = allProgramTypes.filter((t) => !uniqueSelectedTypes.includes(t))

        const canAddSecondProgram =
          Boolean(availableProgramsForGrade) &&
          allProgramTypes.length > 1 &&
          (kid.programs?.length ?? 0) === 1 &&
          uniqueSelectedTypes.length === 1 &&
          remainingTypes.length === 1

        const programs = kid.programs ?? []
        const hasOnCampusProgram = programs.some((p) => p.programType === 'OnCampus')
        const hasOffCampusProgram = programs.some((p) => p.programType === 'OffCampus')

        const kidHybridOption: '' | 'Off-Campus' | 'On-Campus' | 'Hybrid' = supportsHybridOption
          ? (hasOnCampusProgram && hasOffCampusProgram)
              ? 'Hybrid'
              : hasOffCampusProgram
                  ? 'Off-Campus'
                  : hasOnCampusProgram
                      ? 'On-Campus'
                      : ''
          : ''

        const onCampusDays = programs
          .filter((p) => p.programType === 'OnCampus')
          .map((p) => inferDaysPerWeekFromProgramName(p.programName))
          .find((v) => v != null) ?? null

        const isHybridMode = supportsHybridOption && hasOnCampusProgram && hasOffCampusProgram

        const showReduceFiveToFour = hasOnCampusProgram && onCampusDays === 5
        const showTransportation = hasOnCampusProgram
        const showFinaleTrip = hasOffCampusProgram

        return (
          <div className="attendeeForm" key={kid.id}>
            <h3 className="attendeeFormTitle">{kid.id}</h3>
            <div className="attendeeFormRow">
              <div className="inputFieldColumns">
                <div className="inputFieldArea">
                  <label className="fieldLabel">Grade entering Fall 2027</label>
                  <div
                    className="selectInputFieldArea"
                    onClick={() => toggleDropdown(`grade-${kid.id}`)}
                  >
                    {kid.grade?.length ? kid.grade : 'Select...'}
                    <img className="arrowIcon" src={arrowRight} alt="" />
                  </div>
                  {isDropdownOpen(`grade-${kid.id}`) && (
                    <div className="dropdownContainer" ref={setDropdownContainerRef(`grade-${kid.id}`)}>
                      {availableGrades.map((g) => (
                        <div
                          className="dropdownOption"
                          key={g}
                          onClick={() => {
                            dispatch(ivyLeagueSlice.actions.setKidGrade({ kidId: kid.id, grade: g }))
                            closeDropdown()
                          }}
                        >
                          {g}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {supportsHybridOption && (
                  <div className="inputFieldArea">
                    <label className="fieldLabel">Program Option</label>
                    <div
                      className="selectInputFieldArea"
                      onClick={() => toggleDropdown(`hybridOption-${kid.id}`)}
                    >
                      {kidHybridOption || 'Select...'}
                      <img className="arrowIcon" src={arrowRight} alt="" />
                    </div>
                    {isDropdownOpen(`hybridOption-${kid.id}`) && (
                      <div className="dropdownContainer" ref={setDropdownContainerRef(`hybridOption-${kid.id}`)}>
                        {['On-Campus', 'Off-Campus', 'Hybrid'].map((opt) => (
                          <div
                            className="dropdownOption"
                            key={opt}
                            onClick={() => {
                              const firstProgramId = kid.programs?.[0]?.programId
                              if (!firstProgramId) {
                                closeDropdown()
                                return
                              }

                              const keepOnlyProgramType = (programType: 'OnCampus' | 'OffCampus') => {
                                const programs = kid.programs ?? []
                                const existing = programs.find((p) => p.programType === programType)
                                const keepId = existing?.programId

                                if (keepId) {
                                  const other = programs.find((p) => p.programId !== keepId)
                                  if (other?.programId) {
                                    dispatch(ivyLeagueSlice.actions.removeProgramOfaKid({
                                      kidId: kid.id,
                                      programId: other.programId
                                    }))
                                  }
                                  return
                                }

                                // No existing matching entry: repurpose first slot (resets name/weeks) and remove any second.
                                dispatch(ivyLeagueSlice.actions.setProgramTypeOfaKid({
                                  kidId: kid.id,
                                  programId: firstProgramId,
                                  programType
                                }))

                                const second = programs?.[1]
                                if (second?.programId) {
                                  dispatch(ivyLeagueSlice.actions.removeProgramOfaKid({
                                    kidId: kid.id,
                                    programId: second.programId
                                  }))
                                }
                              }

                              if (opt === 'Off-Campus') {
                                keepOnlyProgramType('OffCampus')
                              }

                              if (opt === 'On-Campus') {
                                keepOnlyProgramType('OnCampus')
                              }

                              if (opt === 'Hybrid') {
                                // Ensure we have one OnCampus and one OffCampus entry.
                                const currentPrograms = kid.programs ?? []
                                const p1 = currentPrograms[0]
                                const p2 = currentPrograms[1]

                                if (!p1) {
                                  closeDropdown()
                                  return
                                }

                                // If only one slot exists, ensure it has a type and add the other.
                                if (!p2) {
                                  const firstType = p1.programType || 'OnCampus'
                                  if (!p1.programType) {
                                    dispatch(ivyLeagueSlice.actions.setProgramTypeOfaKid({
                                      kidId: kid.id,
                                      programId: p1.programId,
                                      programType: firstType
                                    }))
                                  }
                                  const missingType = firstType === 'OnCampus' ? 'OffCampus' : 'OnCampus'
                                  dispatch(ivyLeagueSlice.actions.addProgramOfaKid({
                                    kidId: kid.id,
                                    programType: missingType
                                  }))
                                } else {
                                  // Two slots exist: make them complementary without wiping an already-correct entry.
                                  const t1 = (p1.programType ?? '').trim()
                                  const t2 = (p2.programType ?? '').trim()

                                  const setTypeIfNeeded = (programId: string, nextType: 'OnCampus' | 'OffCampus') => {
                                    const current = currentPrograms.find((p) => p.programId === programId)?.programType
                                    if (current === nextType) return
                                    dispatch(ivyLeagueSlice.actions.setProgramTypeOfaKid({
                                      kidId: kid.id,
                                      programId,
                                      programType: nextType
                                    }))
                                  }

                                  if (!t1 && !t2) {
                                    // Both empty: force p1=OnCampus, p2=OffCampus.
                                    setTypeIfNeeded(p1.programId, 'OnCampus')
                                    setTypeIfNeeded(p2.programId, 'OffCampus')
                                  } else if (!t1 && (t2 === 'OnCampus' || t2 === 'OffCampus')) {
                                    setTypeIfNeeded(p1.programId, t2 === 'OnCampus' ? 'OffCampus' : 'OnCampus')
                                  } else if ((t1 === 'OnCampus' || t1 === 'OffCampus') && !t2) {
                                    setTypeIfNeeded(p2.programId, t1 === 'OnCampus' ? 'OffCampus' : 'OnCampus')
                                  } else if (t1 === t2 && (t1 === 'OnCampus' || t1 === 'OffCampus')) {
                                    // Same type: flip the second.
                                    setTypeIfNeeded(p2.programId, t1 === 'OnCampus' ? 'OffCampus' : 'OnCampus')
                                  }
                                  // Else: already one OnCampus and one OffCampus, no change.
                                }

                                // If OffCampus is already present but invalid (<2 weeks), force re-select.
                                const offProgram = (kid.programs ?? []).find((p) => p.programType === 'OffCampus')
                                if (offProgram && (offProgram.weekCount ?? 0) > 0 && (offProgram.weekCount ?? 0) < HYBRID_OFF_CAMPUS_MIN_WEEKS) {
                                  dispatch(ivyLeagueSlice.actions.setWeekCountOfaKid({
                                    kidId: kid.id,
                                    programId: offProgram.programId,
                                    weekCount: 0
                                  }))
                                }
                              }

                              closeDropdown()
                            }}
                          >
                            {opt}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {availableProgramsForGrade && kid.programs.map((program) => (
                  <div key={program.programId} style={{ width: '100%' }}>
                    {(() => {
                      const campusLabel = program.programType === 'OnCampus'
                        ? 'On-Campus'
                        : program.programType === 'OffCampus'
                          ? 'Off-Campus'
                          : ''

                      const isHybridModeForSection = supportsHybridOption && hasOnCampusProgram && hasOffCampusProgram
                      const hybridSectionTitle = isHybridModeForSection
                        ? (program.programType === 'OnCampus'
                            ? 'On-Campus'
                            : program.programType === 'OffCampus'
                              ? 'Off-Campus'
                              : '')
                        : ''

                      const programTypeOptions = Object.keys(availableProgramsForGrade ?? {})
                      const otherSelectedTypes = (kid.programs ?? [])
                        .filter((p) => p.programId !== program.programId)
                        .map((p) => p.programType)
                        .filter((t) => t && t.length > 0)
                      const allowedTypeOptions = programTypeOptions.filter(
                        (t) => t === program.programType || !otherSelectedTypes.includes(t)
                      )

                      const programNameOptions = Object.keys(availableProgramsForGrade?.[program.programType] ?? {})
                      const rawWeekOptions = Object.keys(availableProgramsForGrade?.[program.programType]?.[program.programName] ?? {})
                        .map((w) => parseInt(w, 10))
                        .filter((n) => !isNaN(n))
                        .sort((a, b) => a - b)

                      const isHybridMode = supportsHybridOption && hasOnCampusProgram && hasOffCampusProgram
                      const otherProgram = (kid.programs ?? []).find((p) => p.programId !== program.programId)
                      const otherWeeks = otherProgram?.weekCount ?? 0

                      const weekOptions = (() => {
                        if (!isHybridMode) {
                          // Non-hybrid: weekCount represents total weeks; use pricing table keys.
                          return rawWeekOptions
                        }

                        // Hybrid: weekCount represents per-campus weeks.
                        const isOff = program.programType === 'OffCampus'
                        const allowed = isOff ? HYBRID_OFF_CAMPUS_WEEK_OPTIONS : HYBRID_ON_CAMPUS_WEEK_OPTIONS
                        const otherAllowed = isOff ? HYBRID_ON_CAMPUS_WEEK_OPTIONS : HYBRID_OFF_CAMPUS_WEEK_OPTIONS

                        // If both program names are selected, enforce that the resulting TOTAL weeks exists
                        // in BOTH pricing tables (because proration uses totalWeeks pricing).
                        const onProgram = (kid.programs ?? []).find((p) => p.programType === 'OnCampus')
                        const offProgram = (kid.programs ?? []).find((p) => p.programType === 'OffCampus')
                        const onName = onProgram?.programName
                        const offName = offProgram?.programName

                        const totalWeekKeysFor = (programType: 'OnCampus' | 'OffCampus', programName?: string): Set<number> | null => {
                          if (!programName) return null
                          const keys = Object.keys(availableProgramsForGrade?.[programType]?.[programName] ?? {})
                            .map((w) => parseInt(w, 10))
                            .filter((n) => !isNaN(n))
                          return new Set(keys)
                        }

                        const onTotals = totalWeekKeysFor('OnCampus', onName)
                        const offTotals = totalWeekKeysFor('OffCampus', offName)

                        const isTotalWeekAvailable = (totalWeeks: number): boolean => {
                          if (!onTotals || !offTotals) return true
                          return onTotals.has(totalWeeks) && offTotals.has(totalWeeks)
                        }

                        const isValidTotal = (totalWeeks: number): boolean => {
                          return totalWeeks >= HYBRID_TOTAL_WEEKS_MIN && totalWeeks <= HYBRID_TOTAL_WEEKS_MAX
                        }

                        const otherSelected = otherWeeks > 0

                        return allowed.filter((w) => {
                          if (otherSelected) {
                            const total = w + otherWeeks
                            return isValidTotal(total) && isTotalWeekAvailable(total)
                          }

                          // Other side not selected yet: only show options that can pair with SOME valid other value.
                          return otherAllowed.some((ow) => {
                            const total = w + ow
                            return isValidTotal(total) && isTotalWeekAvailable(total)
                          })
                        })
                      })()

                      const selectedWeekValue = program.weekCount > 0 ? program.weekCount : ''
                      const isOnlyOneProgramType = programTypeOptions.length === 1
                      const isOnlyOneProgramName = programNameOptions.length === 1
                      const isOnlyOneWeekOption = weekOptions.length === 1

                      const typeDropdownKey = `type-${kid.id}-${program.programId}`
                      const nameDropdownKey = `name-${kid.id}-${program.programId}`
                      const weeksDropdownKey = `weeks-${kid.id}-${program.programId}`

                      const programTypeDisabled = isOnlyOneProgramType || allowedTypeOptions.length <= 1
                      const programNameDisabled = program.programType?.length === 0 || isOnlyOneProgramName
                      const weeksDisabled =
                        program.programType?.length === 0 ||
                        program.programName?.length === 0 ||
                        isOnlyOneWeekOption
                      const showProgramNameDropdown = program.programType !== 'OffCampus'

                      return (
                        <>
                          {isHybridModeForSection && hybridSectionTitle && (
                            <div className="hybridSectionHeader">
                              <div className="hybridSectionTitle">{hybridSectionTitle}</div>
                              <div className="hybridSectionRule" />
                            </div>
                          )}
                          {!supportsHybridOption && (
                            <div className={`inputFieldArea ${programTypeDisabled ? 'disabled' : ''}`}>
                              <label className="fieldLabel">Select Program Type</label>
                              <div
                                className="selectInputFieldArea"
                                onClick={() => toggleDropdown(typeDropdownKey, programTypeDisabled)}
                              >
                                {program.programType?.length ? program.programType : 'Select...'}
                                <img className="arrowIcon" src={arrowRight} alt="" />
                              </div>
                              {isDropdownOpen(typeDropdownKey) && (
                                <div className="dropdownContainer" ref={setDropdownContainerRef(typeDropdownKey)}>
                                  {allowedTypeOptions.map((opt) => (
                                    <div
                                      className="dropdownOption"
                                      key={opt}
                                      onClick={() => {
                                        dispatch(ivyLeagueSlice.actions.setProgramTypeOfaKid({
                                          kidId: kid.id,
                                          programId: program.programId,
                                          programType: opt
                                        }))
                                        closeDropdown()
                                      }}
                                    >
                                      {opt}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {showProgramNameDropdown && (
                            <div className={`inputFieldArea ${programNameDisabled ? 'disabled' : ''}`}>
                              <label className="fieldLabel">{campusLabel ? `${campusLabel}: ` : ''}Select Program</label>
                              <div
                                className="selectInputFieldArea"
                                onClick={() => toggleDropdown(nameDropdownKey, programNameDisabled)}
                              >
                                {program.programName?.length ? program.programName : 'Select...'}
                                <img className="arrowIcon" src={arrowRight} alt="" />
                              </div>
                              {isDropdownOpen(nameDropdownKey) && (
                                <div className="dropdownContainer" ref={setDropdownContainerRef(nameDropdownKey)}>
                                  {programNameOptions.map((opt) => (
                                    <div
                                      className="dropdownOption"
                                      key={opt}
                                      onClick={() => {
                                        dispatch(ivyLeagueSlice.actions.setProgramNameOfaKid({
                                          kidId: kid.id,
                                          programId: program.programId,
                                          programName: opt
                                        }))
                                        closeDropdown()
                                      }}
                                    >
                                      {opt}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          <div className={`inputFieldArea ${weeksDisabled ? 'disabled' : ''}`}>
                            <label className="fieldLabel">{campusLabel ? `${campusLabel}: ` : ''}Select Number of Weeks</label>
                            <div
                              className="selectInputFieldArea"
                              onClick={() => toggleDropdown(weeksDropdownKey, weeksDisabled)}
                            >
                              {selectedWeekValue ? String(selectedWeekValue) : 'Select...'}
                              <img className="arrowIcon" src={arrowRight} alt="" />
                            </div>
                            {isDropdownOpen(weeksDropdownKey) && (
                              <div className="dropdownContainer" ref={setDropdownContainerRef(weeksDropdownKey)}>
                                {weekOptions.map((w) => (
                                  <div
                                    className="dropdownOption"
                                    key={w}
                                    onClick={() => {
                                      dispatch(ivyLeagueSlice.actions.setWeekCountOfaKid({
                                        kidId: kid.id,
                                        programId: program.programId,
                                        weekCount: w
                                      }))
                                      closeDropdown()
                                    }}
                                  >
                                    {w}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {isHybridMode && program.programType === 'OnCampus' && (
                            <>
                              <div className="inputFieldArea">
                                <label className="fieldLabel">On-Campus: Transportation</label>
                                <div
                                  className="selectInputFieldArea"
                                  onClick={() => toggleDropdown(`transport-${kid.id}`)}
                                >
                                  {(kid.transportMode ?? 'Parent') === 'Bus'
                                    ? 'Bus'
                                    : 'Parent drop-off/pick-up'}
                                  <img className="arrowIcon" src={arrowRight} alt="" />
                                </div>
                                {isDropdownOpen(`transport-${kid.id}`) && (
                                  <div className="dropdownContainer" ref={setDropdownContainerRef(`transport-${kid.id}`)}>
                                    <div
                                      className="dropdownOption"
                                      onClick={() => {
                                        dispatch(ivyLeagueSlice.actions.setKidTransportMode({
                                          kidId: kid.id,
                                          transportMode: 'Parent'
                                        }))
                                        closeDropdown()
                                      }}
                                    >
                                      Parent drop-off/pick-up
                                    </div>
                                    {!isToddlerGrade(kid.grade) && (
                                      <div
                                        className="dropdownOption"
                                        onClick={() => {
                                          dispatch(ivyLeagueSlice.actions.setKidTransportMode({
                                            kidId: kid.id,
                                            transportMode: 'Bus' as TransportMode
                                          }))
                                          closeDropdown()
                                        }}
                                      >
                                        Bus
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {showReduceFiveToFour && (
                                <div className="sessionWeeksColumn">
                                  <label className="fieldLabel">On-Campus: Reduce 5-day to 4-day schedule?</label>
                                  <div className="sessionsWeeksContainer">
                                    <div className="sessionRow">
                                      <div className="sessionWeek">
                                        <div className="checkboxArea ivyLeagueCheckbox">
                                          <input
                                            type="checkbox"
                                            checked={kid.usesExtendedHours ?? false}
                                            className="checkbox"
                                            onChange={() => dispatch(ivyLeagueSlice.actions.setKidUsesExtendedHours({
                                              kidId: kid.id,
                                              usesExtendedHours: !(kid.usesExtendedHours ?? false)
                                            }))}
                                          />
                                          <label
                                            className="checkmark"
                                            onClick={() => dispatch(ivyLeagueSlice.actions.setKidUsesExtendedHours({
                                              kidId: kid.id,
                                              usesExtendedHours: !(kid.usesExtendedHours ?? false)
                                            }))}
                                          />
                                        </div>
                                        Yes
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {isHybridMode && program.programType === 'OffCampus' && (
                            <>
                              <div className="sessionWeeksColumn">
                                <label className="fieldLabel">Post Grad Finale Trip?</label>
                                <div className="sessionsWeeksContainer">
                                  <div className="sessionRow">
                                    <div className="sessionWeek">
                                      <div className="checkboxArea ivyLeagueCheckbox">
                                        <input
                                          type="checkbox"
                                          checked={kid.finaleTrip ?? false}
                                          className="checkbox"
                                          onChange={() => dispatch(ivyLeagueSlice.actions.setKidFinaleTrip({
                                            kidId: kid.id,
                                            finaleTrip: !(kid.finaleTrip ?? false)
                                          }))}
                                        />
                                        <label
                                          className="checkmark"
                                          onClick={() => dispatch(ivyLeagueSlice.actions.setKidFinaleTrip({
                                            kidId: kid.id,
                                            finaleTrip: !(kid.finaleTrip ?? false)
                                          }))}
                                        />
                                      </div>
                                      Yes
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                    {(kid.programs?.length ?? 0) > 1 && !supportsHybridOption && (
                      <div className="inputFieldArea">
                        <button
                          type="button"
                          className="selectInputFieldArea"
                          style={{ width: '100%', textAlign: 'left' }}
                          onClick={() => dispatch(ivyLeagueSlice.actions.removeProgramOfaKid({
                            kidId: kid.id,
                            programId: program.programId
                          }))}
                        >
                          Remove Program
                        </button>
                      </div>
                    )}
                        </>
                      )
                    })()}
                  </div>
                ))}

                {canAddSecondProgram && !supportsHybridOption && (
                  <div className="inputFieldArea">
                    <button
                      type="button"
                      className="selectInputFieldArea"
                      style={{ width: '100%', textAlign: 'left' }}
                      onClick={() => dispatch(ivyLeagueSlice.actions.addProgramOfaKid({
                        kidId: kid.id,
                        programType: remainingTypes[0]
                      }))}
                    >
                      Add another Campus Program
                    </button>
                  </div>
                )}

                {showTransportation && !isHybridMode && (
                  <div className="inputFieldArea">
                    <label className="fieldLabel">Transportation</label>
                    <div
                      className="selectInputFieldArea"
                      onClick={() => toggleDropdown(`transport-${kid.id}`)}
                    >
                      {(kid.transportMode ?? 'Parent') === 'Bus'
                        ? 'Bus'
                        : 'Parent drop-off/pick-up'}
                      <img className="arrowIcon" src={arrowRight} alt="" />
                    </div>
                    {isDropdownOpen(`transport-${kid.id}`) && (
                      <div className="dropdownContainer" ref={setDropdownContainerRef(`transport-${kid.id}`)}>
                        <div
                          className="dropdownOption"
                          onClick={() => {
                            dispatch(ivyLeagueSlice.actions.setKidTransportMode({
                              kidId: kid.id,
                              transportMode: 'Parent'
                            }))
                            closeDropdown()
                          }}
                        >
                          Parent drop-off/pick-up
                        </div>
                        {!isToddlerGrade(kid.grade) && (
                          <div
                            className="dropdownOption"
                            onClick={() => {
                              dispatch(ivyLeagueSlice.actions.setKidTransportMode({
                                kidId: kid.id,
                                transportMode: 'Bus' as TransportMode
                              }))
                              closeDropdown()
                            }}
                          >
                            Bus
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {showReduceFiveToFour && !isHybridMode && (
                  <div className="sessionWeeksColumn">
                    <label className="fieldLabel">Reduce 5-day to 4-day schedule?</label>
                    <div className="sessionsWeeksContainer">
                      <div className="sessionRow">
                        <div className="sessionWeek">
                          <div className="checkboxArea ivyLeagueCheckbox">
                            <input
                              type="checkbox"
                              checked={kid.usesExtendedHours ?? false}
                              className="checkbox"
                              onChange={() => dispatch(ivyLeagueSlice.actions.setKidUsesExtendedHours({
                                kidId: kid.id,
                                usesExtendedHours: !(kid.usesExtendedHours ?? false)
                              }))}
                            />
                            <label
                              className="checkmark"
                              onClick={() => dispatch(ivyLeagueSlice.actions.setKidUsesExtendedHours({
                                kidId: kid.id,
                                usesExtendedHours: !(kid.usesExtendedHours ?? false)
                              }))}
                            />
                          </div>
                          Yes
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {showFinaleTrip && !isHybridMode && (
                  <div className="sessionWeeksColumn">
                    <label className="fieldLabel">Post Grad Finale Trip?</label>
                    <div className="sessionsWeeksContainer">
                      <div className="sessionRow">
                        <div className="sessionWeek">
                          <div className="checkboxArea ivyLeagueCheckbox">
                            <input
                              type="checkbox"
                              checked={kid.finaleTrip ?? false}
                              className="checkbox"
                              onChange={() => dispatch(ivyLeagueSlice.actions.setKidFinaleTrip({
                                kidId: kid.id,
                                finaleTrip: !(kid.finaleTrip ?? false)
                              }))}
                            />
                            <label
                              className="checkmark"
                              onClick={() => dispatch(ivyLeagueSlice.actions.setKidFinaleTrip({
                                kidId: kid.id,
                                finaleTrip: !(kid.finaleTrip ?? false)
                              }))}
                            />
                          </div>
                          Yes
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {config.env === 'development' && availableProgramsForGrade && (
                  (() => {
                    // Dev-only: show kid total based on proration rule.
                    // Example: 3 on-campus + 3 off-campus => totalWeeks=6.
                    // For each program: use the 6-week base price, prorate by (weeks/totalWeeks).
                    const totalWeeks = (kid.programs ?? []).reduce((s, p) => s + (p.weekCount ?? 0), 0)

                    const proratedLines = (kid.programs ?? []).map((p) => {
                      const base =
                        availableProgramsForGrade?.[p.programType]?.[p.programName]?.[totalWeeks] ??
                        availableProgramsForGrade?.[p.programType]?.[p.programName]?.[String(totalWeeks)]

                      if (!totalWeeks || !p.programType || !p.programName || base == null) {
                        return { programId: p.programId, amount: null as number | null }
                      }

                      const prorated = (Number(base) / totalWeeks) * (p.weekCount ?? 0)
                      return { programId: p.programId, amount: prorated }
                    })

                    const hasAll = totalWeeks > 0 && proratedLines.every((l) => l.amount != null)
                    const kidTotal = hasAll
                      ? proratedLines.reduce((s, l) => s + (l.amount ?? 0), 0)
                      : null

                    return (
                      <div className="inputFieldArea">
                        <label className="fieldLabel">Kid Program Total (prorated)</label>
                        <div>
                          {kidTotal != null ? `$${formatMoney(kidTotal)}` : 'N/A'}
                        </div>
                      </div>
                    )
                  })()
                )}
              </div>
            </div>
          </div>
        )
        })}
      </div>
      {openDropdownKey && (
        <div className="backDrop" onClick={closeDropdown} />
      )}
    </>
  )
}
